import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactElement } from 'react';

import { cn } from '../lib/cn';

/**
 * 버튼.
 *
 * 주 버튼은 민트 면 + 잉크 글자다 — 민트는 글자로 쓸 수 없고(흰 배경 1.29:1)
 * 면으로 깔았을 때만 15.48:1 이 나온다.
 * 부 버튼은 색이 아니라 "반전"으로 상태를 만든다.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ' +
    'tracking-[-0.014em] transition-colors ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ' +
    'disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground font-semibold hover:bg-primary/85 ' +
          'disabled:bg-border disabled:text-muted-foreground',
        secondary:
          'border border-foreground text-foreground hover:bg-foreground hover:text-background ' +
          'disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent',
        ghost: 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        danger:
          'border border-destructive text-destructive ' +
          'hover:bg-destructive hover:text-destructive-foreground ' +
          // 비활성이 활성과 똑같아 보이면 눌러도 아무 일이 없는 이유를 화면이 설명하지
          //   못한다. 되돌릴 수 없는 조치에 쓰이는 변형이라 특히 그렇다.
          'disabled:border-border disabled:text-muted-foreground ' +
          'disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
      },
      size: {
        sm: 'h-8 px-[13px] text-[12.5px] rounded-sm',
        md: 'h-10 px-[18px] text-sm',
        lg: 'h-12 px-6 text-[15px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export function Button({ className, variant, size, type, ...props }: ButtonProps): ReactElement {
  return (
    <button
      // 폼 안에서 의도치 않게 제출되지 않도록 기본값을 명시한다
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
